import { useMutation, useQuery } from "@tanstack/react-query";

import { api } from "@/services/api";
import type { EmailTemplateDetail, EmailTemplateListItem } from "@/types";

/**
 * React Query hooks for the admin Email Templates catalog — a read-only view of
 * the programme participant email sequence. Wraps the existing API client
 * methods and owns the query keys + caching. There are no mutations: the catalog
 * is read-only. Follows the Support slice template (list + detail-by-id).
 */

export const emailTemplateKeys = {
  all: ["admin", "email-templates"] as const,
  list: () => [...emailTemplateKeys.all, "list"] as const,
  detail: (id: string) => [...emailTemplateKeys.all, "detail", id] as const,
};

export function useEmailTemplates() {
  return useQuery<EmailTemplateListItem[]>({
    queryKey: emailTemplateKeys.list(),
    queryFn: () => api.getEmailTemplates(),
  });
}

export function useEmailTemplate(id: string | undefined) {
  return useQuery<EmailTemplateDetail>({
    queryKey: emailTemplateKeys.detail(id ?? ""),
    queryFn: () => api.getEmailTemplate(id as string),
    enabled: Boolean(id),
  });
}

export function usePreviewEmail() {
  return useMutation({
    mutationFn: ({ id, context }: { id: string; context: Record<string, string> }) =>
      api.previewEmailTemplate(id, context),
  });
}

export function useAdHocSend() {
  return useMutation({
    mutationFn: (payload: {
      template_id: string;
      context: Record<string, string>;
      programme_id?: string;
      user_id?: string;
    }) => api.adHocSendEmail(payload),
  });
}
